import React from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { Button, FlexTableCol, FlexTableRow, PageWrapper, Paper, Row } from 'virtues-bootstrap';
import PageHeader from 'components/ui/PageHeader';
import { handleCVReportsData } from 'store/selectors/reports/recent';
import { requestCardiovascularReports } from 'services/api/request/reports';
import usePagination from 'hook/usePagination';
import PaperWithLoader from 'components/ui/PaperWithLoader';
import PaginationSection from 'components/ui/PaginationSection';
import { ReactComponent as IconFileDownload } from 'svg/file-download-solid.svg';
import { ReactComponent as IconFileDelete } from 'svg/trash.svg';
import DeleteCardioVascularReport from 'components/DeleteCardioVascularReport';
import ReportManagementPanel from './ReportManagementPanel';
import HeaderColumn from './HeaderColumn';
import s from './styles.module.scss';

function CardiovascularReports({
  isAvailableManageReports,
  handleDownloadDocument,
  handleRemoveReportClick,
  patientId,
  patientName,
  reportTypes,
  userId,
  match,
  t,
}) {
  const [
    { data, initial, fetching, pagination, error, sorting },
    getData,
    dispatch,
  ] = usePagination({
    request: requestCardiovascularReports,
    params: { subject: patientId, type: reportTypes, sort: '_lastUpdated', userId },
    selector: handleCVReportsData,
  });
  const { total, page, count } = pagination;
  const { sort, desc } = sorting;

  const handlePageChange = selectedPage => {
    getData({
      pagesOffset: count * selectedPage,
      page: selectedPage,
    });
  };

  const headerColumns = [
    { title: t('uploadDate'), key: '_lastUpdated' },
    { title: t('reportDate') },
    { title: t('reports.reportType') },
  ];

  return (
    <>
      <DeleteCardioVascularReport onPageChange={dispatch} />
      <PageWrapper>
        <PageHeader title={t('cardiovascularInvestigations.pageTitle')} />
        {isAvailableManageReports ? (
          <ReportManagementPanel patientName={patientName} patientId={patientId} />
        ) : (
          <Row className={s.patientInfo} center="xs">{`RPM-CIED-${patientId}`}</Row>
        )}
        <PaperWithLoader
          error={error}
          loading={initial || fetching}
          check={total}
          placeholder={t('reports.reportsNotFound')}
        >
          <Paper className={s.mainPaper}>
            <FlexTableRow heading>
              {headerColumns.map(({ title, key }) => (
                <HeaderColumn
                  key={title}
                  sort={sort}
                  currentKey={key}
                  title={title}
                  desc={desc}
                  handleClick={getData}
                />
              ))}
            </FlexTableRow>
            {data.map(({ id, lastUpdated, type, reportDate }) => (
              <FlexTableRow key={id} to={`/patient/${match.params.id}/transmission-report/${id}`}>
                <FlexTableCol>
                  <strong>{lastUpdated}</strong>
                </FlexTableCol>
                <FlexTableCol>
                  <strong>{reportDate}</strong>
                </FlexTableCol>
                <FlexTableCol className={s.row}>
                  <span>{t(`cardiovascularInvestigations.${type}`)}</span>
                  <span>
                    <Button
                      icon={<IconFileDownload />}
                      variant="link-secondary"
                      size="sm"
                      onClick={handleDownloadDocument(id)}
                    >
                      {t('download')}
                    </Button>
                    {isAvailableManageReports && (
                      <Button
                        icon={<IconFileDelete />}
                        variant="danger"
                        size="sm"
                        outlined
                        onClick={handleRemoveReportClick(id)}
                      >
                        {t('Remove')}
                      </Button>
                    )}
                  </span>
                </FlexTableCol>
              </FlexTableRow>
            ))}
          </Paper>
          <PaginationSection
            activePage={page}
            disableInitialCallback
            onPageChange={handlePageChange}
            itemsPerPage={count}
            textKey="pagination.reports"
            total={total}
          />
        </PaperWithLoader>
      </PageWrapper>
    </>
  );
}

CardiovascularReports.propTypes = {
  isAvailableManageReports: PropTypes.bool.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  patientId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  reportTypes: PropTypes.string.isRequired,
  patientName: PropTypes.string.isRequired,
  handleDownloadDocument: PropTypes.func.isRequired,
  handleRemoveReportClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default CardiovascularReports;
